export const invoiceList = {
  head: {
    title: "Invoicing",
    subtitle: "Invoices issued to your guests",
  },
  stats: {
    filterAria: "Filter invoices by status",
    total: "Total",
    draft: "Drafts",
    sent: "Issued",
    processing: "Processing",
    error: "With errors",
    pending: "Pending",
    voided: "Voided",
  },
  toolbar: {
    searchLabel: "Search invoices",
    searchPlaceholder: "Invoice no., client, email, tax ID or booking…",
    clearSearch: "Clear search",
    propertyLabel: "Accommodation",
    allProperties: "All accommodations",
    typeToSearchProperties: "Type to search accommodations.",
    noProperties: "No accommodations found.",
    statusLabel: "Status",
    emisorLabel: "Issuer",
    allEmisores: "All issuers",
    servicesTitle: "Catalog of reusable charges and services",
    services: "Charges and services",
    createInvoice: "Create invoice",
  },
  exportZip: {
    button: "ZIP",
    title: "One file per invoice, each with its date and booking code, all inside a single ZIP",
    generating: "Generating…",
    zipping: "Compressing…",
    noPdfInPeriod: "There are no issued invoices with a PDF in this period.",
    noPdfInPage: "There are no issued invoices with a PDF on this page.",
    tooMany: "The selection has {total} invoices and the ZIP allows {limit}. Narrow the period or the filters.",
    noneDownloaded: "No invoice could be downloaded from Invopop. Try again in a few minutes.",
    failed: "{n} invoice(s) could not be downloaded from Invopop: {codes}.",
    withoutPdf: "{n} invoice(s) do not have a PDF in Invopop yet and are not in the ZIP: {codes}.",
    restIncluded: "All the others are in the ZIP.",
    error: "The invoice ZIP could not be generated.",
  },
  linkedClient: {
    prefix: "Invoices linked to",
    fallbackName: "this billing client",
    clear: "Remove client filter",
  },
  table: {
    numberHeader: "INVOICE NO.",
    customerHeader: "CLIENT",
    propertyHeader: "ACCOMMODATION",
    totalHeader: "TOTAL",
    statusHeader: "STATUS",
    dateHeader: "DATE",
    actionsHeader: "ACTIONS",
    draftReferenceTitle: "Draft reference. The tax number is assigned when issued.",
    openReserva: "Open the booking",
    viewCreditNote: "View corrective invoice {code}",
    creditNote: "Corrective invoice",
    viewError: "View error",
    voids: "Voids",
    correctionOf: "Corr. of",
    correctedBy: "Corrected by:",
  },
  sortFields: {
    number: "invoice number",
    customer: "client",
    property: "accommodation",
    total: "total",
    status: "status",
    date: "date",
  },
  sort: {
    sortBy: "Sort by {label}",
    sortedBy: "Sorted by {label}, {direction}. Change the order",
    asc: "ascending",
    desc: "descending",
  },
  actions: {
    openPdf: "Open invoice as PDF",
    editDraftTitle: "Edit client details and line items",
    edit: "Edit",
    previewTitle: "Open a preview without tax validity",
    preview: "Preview",
    sendQuote: "Quote",
    sendQuoteTitle: "Email the quote to the client",
    emitTitle: "Review and issue the invoice",
    emit: "Issue",
    syncTitle: "Refresh the invoice status",
    sync: "Refresh",
    retryTitle: "Retry with updated billing details",
    retry: "Retry",
    moreOptions: "More options",
    moreOptionsAria: "More invoice options",
    editCustomer: "Edit client details",
    deleteReset: "Delete and reset request",
    voidReissue: "Void and reissue",
  },
  empty: {
    searchTitle: "No invoices found",
    searchText: "No results match the current search and filters.",
    clearFilters: "Clear filters",
    title: "No invoices yet",
    text: "Create a draft and issue it once the tax details and charges are ready.",
    createFirst: "Create your first invoice",
  },
  errorModal: {
    title: "Invoice error",
    hintBefore: "If the error is in your billing details (tax ID, series…), correct them in",
    hintSettingsPath: "Settings → Invoicing",
    hintBetween: "and then use",
    hintRetryWord: "Retry",
    hintGuest: "If the error is in the guest's details, use",
    hintEditWord: "Edit client details",
    close: "Close",
    retryNow: "Retry now",
  },
  editCustomer: {
    title: "Edit client details",
    subtitle: "Correct the details and retry the invoice",
    name: "Name / Legal name *",
    taxId: "Tax ID / VAT number",
    country: "Country (2-letter code) *",
    email: "Email *",
    address: "Address",
    city: "City",
    zip: "Postal code",
    hint: "On saving, sending the invoice will be retried automatically.",
    cancel: "Cancel",
    saving: "Saving…",
    save: "Save and retry",
  },
  deleteConfirm: {
    title: "Delete invoice",
    body1: "This invoice attempt will be deleted and the",
    bodyStrong: "guest's request will be reset",
    body2: ", letting them enter their details and request the invoice again.",
    irreversible: "This action cannot be undone.",
    cancel: "Cancel",
    deleting: "Deleting…",
    confirm: "Yes, delete",
  },
  create: {
    titleEdit: "Edit draft",
    titleCreate: "Create invoice",
    subtitleEdit: "Update the client, context and line items. It is not issued.",
    subtitleCreate: "Draft — no number until issued. No booking required.",
    loading: "Loading draft…",
    clientLabel: "Client *",
    clientHelp: "Select a saved billing profile or create a new one.",
    newClient: "New client",
    company: "Company",
    individual: "Individual",
    change: "Change",
    clientSearchPlaceholder: "Search by name, email or document…",
    incomplete: "Incomplete",
    noClients: "No clients found.",
    createClient: "Create client",
    contextTitle: "Invoice context",
    optional: "Optional",
    contextHelp: "Link this invoice to keep its traceability. You can leave both fields empty.",
    propertyLabel: "Accommodation",
    propertyPlaceholder: "Search accommodation…",
    typeToSearchProperties: "Type to search accommodations.",
    noProperties: "No accommodations found.",
    reservationLabel: "Booking",
    reservationPlaceholder: "Search code, guest, email…",
    noHolder: "No holder",
    typeToSearchReservations: "Type to search bookings.",
    noReservations: "No bookings found.",
    contextNotice: "The booking sets the accommodation and adds the stay if it has not been invoiced yet.",
    chargesTitle: "Charges and services",
    chargesHelp: "Select items saved in your catalog.",
    addCharge: "Add charge or service",
    colConcept: "Item",
    colQty: "Qty",
    colUnitPrice: "Unit price",
    colTotal: "Total",
    docTitle: "How the invoice will look",
    docHint: "These are the lines the PDF will carry. Accommodation, discounts and automatic charges come from the booking: above you only edit your own catalogue charges.",
    due: "Outstanding",
    assumed: "No payments are recorded on this booking. The amount is taken as collected from the payment method set, and the PDF will say the same.",
    linesEmpty: "Add one or more items from your catalog.",
    total: "Total",
    cancel: "Cancel",
    saving: "Saving…",
    saveChanges: "Save changes",
    saveDraft: "Save draft",
  },
  contextSummary: {
    property: "Accommodation selected",
    empty: "Add accommodation or booking",
  },
  emit: {
    title: "Issue invoice",
    subtitle: "This action will assign the final tax number.",
    client: "Client",
    property: "Accommodation",
    total: "Total",
    warning: "Once issued you will not be able to edit it directly; any correction will require a corrective document.",
    cancel: "Cancel",
    emitting: "Issuing…",
    confirm: "Issue invoice",
  },
  editDraft: {
    title: "Edit draft",
    subtitle: "Modify the client details and line items. It is not issued.",
    loading: "Loading draft…",
    individual: "Individual",
    individualSub: "Natural person",
    company: "Company",
    companySub: "Legal entity",
    legalName: "Legal name *",
    fullName: "Full name *",
    companyNamePlaceholder: "Company name",
    individualNamePlaceholder: "First and last name",
    country: "Country *",
    docCompany: "Company tax ID (VAT)",
    docIndividual: "ID / Passport",
    docCompanyPlaceholder: "VAT / Tax ID",
    docIndividualPlaceholder: "National ID or passport",
    email: "Email",
    emailOptional: "(optional)",
    lodgingTitle: "Accommodation",
    lodgingName: "Accommodation",
    lodgingNoteBefore: "Taken from the booking",
    lodgingNoteAfter: ". To change this amount, edit it in the booking.",
    chargesTitle: "Charges and services",
    chargesHelp: "Add saved items without altering the accommodation.",
    addCharge: "Add charge or service",
    colConcept: "Item",
    colQty: "Qty",
    colUnitPrice: "Unit price",
    colTotal: "Total",
    linesEmpty: "No additional items.",
    linesEmptyReserva: "The invoice includes only the accommodation.",
    total: "Total",
    cancel: "Cancel",
    saving: "Saving…",
    saveChanges: "Save changes",
  },
  void: {
    doneTitle: "Invoice voided",
    doneSubtitle: "Corrective invoice issued successfully",
    whatHappened: "What happened?",
    doneCreditNote: "The corrective invoice was issued",
    doneListed: "The invoice stays in the list marked as voided",
    doneCharges: "The additional charges have been released",
    doneGuest: "The guest can request their invoice again",
    nextStepsLabel: "Next steps:",
    nextStepsText: "You can add or correct charges on the booking. Then resend the billing link to the guest.",
    understood: "Got it",
    title: "Void and reissue invoice",
    introBefore: "This action",
    introStrong: "will void the issued invoice",
    introAfter: "by means of an official corrective invoice.",
    willEmit: "A corrective invoice will be issued",
    willList: "The invoice will stay in the list marked as voided",
    willFree: "The additional charges will be released",
    willAllowGuest: "The guest will be able to request the invoice again",
    reasonLabel: "Reason for voiding",
    reasonOther: "Other reason…",
    reasonPlaceholder: "Explain why you are voiding this invoice",
    warning: "The corrective invoice is an official tax document. Use it only if you really need to correct the invoice.",
    cancel: "Cancel",
    emitting: "Issuing corrective invoice…",
    confirm: "Yes, void and reissue",
  },
  statusFilters: {
    all: "All",
    draft: "Drafts",
    sent: "Issued",
    processing: "Processing",
    error: "With errors",
    pending: "Pending",
    voided: "Voided",
  },
  voidReasons: {
    wrongTaxData: "Incorrect client tax details",
    wrongAmount: "Incorrect amount or charges",
    duplicate: "Duplicate invoice",
    cancelledReservation: "Booking cancelled",
    reissue: "Voided to reissue with corrected details or charges",
  },
  statusLabels: {
    draft: "Draft",
    sent: "Issued",
    processing: "Processing",
    error: "Error",
    pending: "Pending",
    corrected: "Corrected",
    voided: "Voided",
  },
  client: {
    fallbackName: "Client",
    fiscalPending: "Billing profile pending",
    fiscalSaved: "Billing profile saved",
  },
  reservation: {
    noCode: "No code",
    noDates: "No dates",
    noHolder: "No holder",
    propertyFallback: "Accommodation #{id}",
  },
  emisorFallback: "Issuer #{id}",
  toasts: {
    selectClientComplete: "Select a client with a complete billing profile.",
    selectReservationOrCharge: "Select a booking or add at least one charge or service.",
    saveDraftError: "The draft could not be saved.",
    createError: "The invoice could not be created.",
    quoteSent: "Quote sent to {email}",
    quoteError: "The quote could not be sent.",
    previewError: "The preview could not be generated.",
    previewName: "Preview",
    emitRejected: "The tax platform rejected the issuance.",
    emitError: "The invoice could not be issued. Check the tax details and try again.",
    selectClientToContinue: "Select a client with a billing profile to continue.",
    loadDraftError: "The draft details could not be loaded.",
    nameRequired: "Enter the client's name.",
    countryRequired: "Select the client's country.",
    selectCatalogCharge: "Select at least one charge or service from the catalog.",
    retryError: "The retry failed.",
    saveError: "Could not save.",
    voidError: "The invoice could not be voided.",
    deleteError: "Could not delete.",
  },
};
